import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import {
  Home,
  Users,
  Building2,
  Target,
  Settings,
  Plus,
  FileText,
  BarChart3,
  User,
} from 'lucide-react'
// Modules désactivés
// import { useContacts, type Contact } from '@/hooks/useContacts'
// import { useCompanies } from '@/hooks/useCompanies'
import type { Contact, Company } from '@/types'
// import { useDeals } from '@/hooks/useDeals'
// import { getDealDisplayTitle } from '@/utils/deals'

interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({
  open,
  onOpenChange,
}) => {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  
  // Modules désactivés - contacts/companies/deals supprimés
  // const { data: contactsData } = useContacts()
  // const { data: companiesData } = useCompanies()
  // const { data: dealsData } = useDeals()
  // Les hooks normalisent déjà en forme paginée { items: [] }
  const contacts: Contact[] = []
  const companies: Company[] = []
  // const deals: any[] = []

  // Navigation rapide
  const navigationItems = [
    {
      icon: Home,
      label: 'Tableau de bord',
      shortcut: '⌘H',
      action: () => navigate('/'),
    },
    {
      icon: Users,
      label: 'Contacts',
      shortcut: '⌘1',
      action: () => navigate('/contacts'),
    },
    {
      icon: Building2,
      label: 'Entreprises',
      shortcut: '⌘2',
      action: () => navigate('/companies'),
    },
    {
      icon: Target,
      label: 'Deals',
      shortcut: '⌘3',
      action: () => navigate('/deals'),
    },
    {
      icon: BarChart3,
      label: 'Analytics',
      shortcut: '⌘4',
      action: () => navigate('/analytics'),
    },
    {
      icon: Settings,
      label: 'Paramètres',
      shortcut: '⌘,',
      action: () => navigate('/settings'),
    },
  ]

  // Actions rapides
  const quickActions = [
    {
      icon: Plus,
      label: 'Nouveau contact',
      shortcut: '⌘N',
      action: () => {
        navigate('/contacts')
        // Ici on pourrait ouvrir directement le modal de création
      },
    },
    {
      icon: Building2,
      label: 'Nouvelle entreprise',
      shortcut: '⌘⇧N',
      action: () => {
        navigate('/companies')
      },
    },
    {
      icon: Target,
      label: 'Nouveau deal',
      shortcut: '⌘D',
      action: () => {
        navigate('/deals')
      },
    },
    {
      icon: FileText,
      label: 'Nouvelle tâche',
      shortcut: '⌘T',
      action: () => {
        // Action pour créer une tâche
      },
    },
  ]

  // Filtrer les résultats selon la recherche
  const filteredContacts = contacts
    .filter((contact: Contact) =>
      contact.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.email?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .slice(0, 5) // Limiter à 5 résultats

  const filteredCompanies = companies
    .filter((company: Company) =>
      company.name?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .slice(0, 5)

  // Deals désactivés
  const filteredDeals: any[] = []
  // const filteredDeals = deals
  //   .filter((deal: any) =>
  //     getDealDisplayTitle(deal).toLowerCase().includes(searchQuery.toLowerCase())
  //   )
  //   .slice(0, 5)

  const handleItemSelect = (action: () => void) => {
    action()
    onOpenChange(false)
  }

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Rechercher ou naviguer..."
        value={searchQuery}
        onValueChange={setSearchQuery}
      />
      <CommandList>
        <CommandEmpty>Aucun résultat trouvé.</CommandEmpty>
        
        {/* Navigation */}
        <CommandGroup heading="Navigation">
          {navigationItems.map((item) => (
            <CommandItem
              key={item.label}
              onSelect={() => handleItemSelect(item.action)}
            >
              <item.icon className="mr-2 h-4 w-4" />
              <span>{item.label}</span>
              <span className="ml-auto text-xs text-muted-foreground">
                {item.shortcut}
              </span>
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        {/* Actions rapides */}
        <CommandGroup heading="Actions rapides">
          {quickActions.map((action) => (
            <CommandItem
              key={action.label}
              onSelect={() => handleItemSelect(action.action)}
            >
              <action.icon className="mr-2 h-4 w-4" />
              <span>{action.label}</span>
              <span className="ml-auto text-xs text-muted-foreground">
                {action.shortcut}
              </span>
            </CommandItem>
          ))}
        </CommandGroup>

        {searchQuery && (
          <>
            <CommandSeparator />
            
            {/* Résultats de recherche - Contacts */}
            {filteredContacts.length > 0 && (
              <CommandGroup heading="Contacts">
                {filteredContacts.map((contact) => (
                  <CommandItem
                    key={contact.id}
                    onSelect={() =>
                      handleItemSelect(() => navigate(`/contacts/${contact.id}`))
                    }
                  >
                    <User className="mr-2 h-4 w-4" />
                    <div className="flex flex-col">
                      <span>
                        {contact.first_name} {contact.last_name}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {contact.email}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* Résultats de recherche - Entreprises */}
            {filteredCompanies.length > 0 && (
              <CommandGroup heading="Entreprises">
                {filteredCompanies.map((company) => (
                  <CommandItem
                    key={company.id}
                    onSelect={() =>
                      handleItemSelect(() => navigate(`/companies/${company.id}`))
                    }
                  >
                    <Building2 className="mr-2 h-4 w-4" />
                    <div className="flex flex-col">
                      <span>{company.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {company.industry}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* Résultats de recherche - Deals */}
            {filteredDeals.length > 0 && (
              <CommandGroup heading="Deals">
                {filteredDeals.map((deal: any) => (
                  <CommandItem
                    key={deal.id}
                    onSelect={() =>
                      handleItemSelect(() => navigate(`/deals`))
                    }
                  >
                    <Target className="mr-2 h-4 w-4" />
                    <div className="flex flex-col">
                      <span>{deal.title || 'Deal'}</span>
                      <span className="text-xs text-muted-foreground">
                        {deal.company_name} • {deal.amount?.toLocaleString()}€
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </>
        )}
      </CommandList>
    </CommandDialog>
  )
}


